import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTour } from '../../context/TourContext';
import './TourOverlay.css';

const TourOverlay = () => {
  const { t } = useTranslation();
  const {
    isActive,
    currentStepData,
    currentStep,
    totalSteps,
    nextStep,
    prevStep,
    skipTour,
    stopTour,
  } = useTour();

  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const calculatePosition = useCallback(() => {
    if (!currentStepData?.target) return;

    const targetElement = document.querySelector(currentStepData.target);
    if (!targetElement) {
      // If target not found, show tooltip in center
      setTargetRect(null);
      setTooltipPosition({
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      });
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    setTargetRect({
      top: rect.top - 8,
      left: rect.left - 8,
      width: rect.width + 16,
      height: rect.height + 16,
    });

    // Calculate tooltip position based on placement
    const placement = currentStepData.placement || 'bottom';
    const tooltipWidth = 320;
    const tooltipHeight = 180;
    const padding = 16;

    let top, left;

    switch (placement) {
      case 'top':
        top = rect.top - tooltipHeight - padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'bottom':
        top = rect.bottom + padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - padding;
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + padding;
        break;
      default:
        top = rect.bottom + padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
    }

    // Keep tooltip within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left < padding) left = padding;
    if (left + tooltipWidth > viewportWidth - padding) {
      left = viewportWidth - tooltipWidth - padding;
    }
    if (top < padding) top = padding;
    if (top + tooltipHeight > viewportHeight - padding) {
      top = viewportHeight - tooltipHeight - padding;
    }

    setTooltipPosition({ top: `${top}px`, left: `${left}px` });
  }, [currentStepData]);

  useEffect(() => {
    if (isActive && currentStepData) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(calculatePosition, 100);

      // Recalculate on resize
      window.addEventListener('resize', calculatePosition);
      window.addEventListener('scroll', calculatePosition, true);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', calculatePosition);
        window.removeEventListener('scroll', calculatePosition, true);
      };
    }
  }, [isActive, currentStepData, calculatePosition]);

  if (!isActive || !currentStepData) return null;

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  return (
    <div className="tour-overlay">
      {/* Dark backdrop with spotlight hole */}
      <div className="tour-backdrop">
        {targetRect && (
          <div
            className="tour-spotlight"
            style={{
              top: targetRect.top,
              left: targetRect.left,
              width: targetRect.width,
              height: targetRect.height,
            }}
          />
        )}
      </div>

      {/* Tooltip */}
      <div className="tour-tooltip" style={tooltipPosition}>
        <div className="tour-tooltip-header">
          <h3>{t(currentStepData.titleKey)}</h3>
          <button type="button" className="tour-close-btn" onClick={stopTour}>
            <span className="material-icons">close</span>
          </button>
        </div>

        <div className="tour-tooltip-content">
          <p>{t(currentStepData.contentKey)}</p>
        </div>

        <div className="tour-tooltip-footer">
          <div className="tour-progress">
            {Array.from({ length: totalSteps }, (_, i) => (
              <span
                key={i}
                className={`tour-dot ${i === currentStep ? 'active' : ''} ${i < currentStep ? 'completed' : ''}`}
              />
            ))}
          </div>

          <div className="tour-actions">
            {isFirstStep ? (
              <button type="button" className="tour-btn tour-btn-skip" onClick={skipTour}>
                {t('tour.skip')}
              </button>
            ) : (
              <button type="button" className="tour-btn tour-btn-prev" onClick={prevStep}>
                <span className="material-icons">arrow_back</span>
                {t('tour.previous')}
              </button>
            )}

            <button type="button" className="tour-btn tour-btn-next" onClick={nextStep}>
              {isLastStep ? (
                <>
                  {t('tour.finish')}
                  <span className="material-icons">check</span>
                </>
              ) : (
                <>
                  {t('tour.next')}
                  <span className="material-icons">arrow_forward</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourOverlay;
